import React, { useCallback, useEffect, useState } from "react";
import {
  AutocompleteComponent,
  Tables,
  PermissionsComponent,
} from "../../../../../Components";
import { ButtonBase } from "@material-ui/core";
import { filterData, headerData } from "./headerData";
import { useTranslation } from "react-i18next";
import { TableActions, TableFilterTypesEnum } from "../../../../../Enums";
import { isEqual } from "lodash";
import OfferManagementDialog from "./components/OfferManagementDialog/OfferManagementDialog";
import {
  GetFilteredVendorOffers,
  GetHoldingCompanyNames,
  GetLookupItemsByLookupTypeName,
} from "../../../../../Services";
import {
  GetParams,
  returnPropsByPermissions,
  showError,
} from "../../../../../Helper";
import DeleteOfferDialog from "./components/DeleteOfferDialog";
import { VendorsTranslationPath } from "../../I18n/VendorsLocale";
import ExportButton from "./components/ExportButton";
import {
  OffersPermissions,
  VendorsPermissions,
} from "../../../../../Permissions";
import { useDebouncedAction } from "../../../../../Hooks/DebouncedAction";

const Offers = ({ parentTranslationPath }) => {
  parentTranslationPath = parentTranslationPath ?? VendorsTranslationPath;
  const { t } = useTranslation(parentTranslationPath);
  const [data, setData] = useState({ result: [], totalCount: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const vendorId = GetParams("id") ? +GetParams("id") : null;
  const [activeItem, setActiveItem] = useState(null);
  const pathName = window.location.pathname.split("/home/")[1];
  const [managementDialogOpen, setManagementDialogOpen] = useState(false);
  const [deleteOfferDialogOpen, setDeleteOfferDialogOpen] = useState(false);
  const [filter, setFilter] = useState({
    pageIndex: 0,
    pageSize: 25,
    vendorId,
    holdingCompanyName: null,
  });

  const [filtersData, setFiltersData] = useState({
    companyNames: [],
    offerStatus: [],
  });

  const onFilterValuesChanged = (filterValue) => {
    if (filterValue && !!Object.values(filterValue).length) {
      const { value, searchableKey } = Object.values(filterValue)[0];
      setFilter((filter) => ({
        ...filter,
        [searchableKey]: value,
      }));
    } else {
      const updatedFilter = Object.fromEntries(
        Object.entries(filter).filter(([_key, value]) => !!value || value === 0)
      );
      if (!isEqual(updatedFilter, filter)) {
        setFilter(updatedFilter);
      }
    }
  };

  const getFilteredVendorOffers = useCallback(async () => {
    if (returnPropsByPermissions(OffersPermissions.ViewOffers.permissionsId)) {
      try {
        setIsLoading(true);
        const res = await GetFilteredVendorOffers({
          ...filter,
          pageIndex: filter.pageIndex + 1,
        });
        if (res) setData(res);
      } catch (error) {
        showError(t("something_went_wrong"));
      } finally {
        setIsLoading(false);
      }
    }
  }, [filter]);

  const reload = (filterData = {}) => {
    setFilter((prevFilter) => ({
      ...prevFilter,
      pageIndex: 0,
      ...filterData,
    }));
  };

  useEffect(() => {
    getFilteredVendorOffers();
  }, [getFilteredVendorOffers]);

  const getCompanyNames = useDebouncedAction(async (value) => {
    try {
      const res = await GetHoldingCompanyNames({
        search: value,
        pageIndex: 1,
        pageSize: 100,
      });
      setFiltersData((item) => ({ ...item, companyNames: res.result }));
    } catch {
      setFiltersData((item) => ({ ...item, companyNames: [] }));
      showError("get company names error");
    }
  });

  const getTableActionsWithPermissions = () => {
    let list = [];
    if (
      pathName === "vendors/vendor-profile-edit" &&
      returnPropsByPermissions(
        VendorsPermissions.EditOfferInVendorProfile.permissionsId
      )
    ) {
      list.push({ enum: TableActions.edit.key });
    } else if (pathName === "offers")
      list.push({ enum: TableActions.edit.key });
    list.push({ enum: TableActions.delete.key });
    return list;
  };

  const DispalyAddBtnsWithPermissions = () => {
    if (pathName === "vendors/vendor-profile-edit")
      return returnPropsByPermissions(
        VendorsPermissions.CreateOfferInVendorProfile.permissionsId
      );
    else if (pathName === "offers") return true;
  };

  const getLookupData = useCallback(async () => {
    setIsLoading(true);
    const res = await GetLookupItemsByLookupTypeName({
      lookUpName: "OfferStatus",
      pageIndex: 1,
      pageSize: 10000,
    });
    if (!(res && res.status && res.status !== 200)) {
      setFiltersData((item) => ({
        ...item,
        offerStatus: Array.isArray(res.result) ? res.result : [],
      }));
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    getCompanyNames();
    getLookupData();
  }, []);

  return (
    <div className="p-3 flex d-flex-column gap-1">
      <div className="flex fj-between fa-center">
        {DispalyAddBtnsWithPermissions() ? (
          <ButtonBase
            className="btns theme-solid px-3"
            onClick={() => setManagementDialogOpen(true)}
          >
            <span className="mdi mdi-plus" />
            {t(`add_offer`)}
          </ButtonBase>
        ) : (
          <div/>
        )}
        <div className="flex gap-1">
          <AutocompleteComponent
            idRef="CompanyNameRef"
            inputPlaceholder="select company name"
            multiple={false}
            data={filtersData.companyNames}
            inputClasses="fields-content"
            wrapperClasses="w-auto"
            displayLabel={(option) => option}
            onChange={(_, newValue) => {
              setFilter((item) => ({
                ...item,
                holdingCompanyName: newValue,
              }));
            }}
            withLoader
            withoutSearchButton
            onOpen={() => {
              if (filtersData?.companyNames?.length === 0) getCompanyNames();
            }}
            onInputChange={(_e, value) => getCompanyNames(value)}
          />
          <ExportButton
            filter={{
              ...filter,
              pageSize: data.totalCount,
              pageIndex: 0,
              totalCount: data.totalCount,
            }}
          />
        </div>
      </div>

      <Tables
        data={data.result}
        headerData={headerData(
          t,
          getFilteredVendorOffers,
          filtersData.offerStatus
        )}
        totalItems={data.totalCount}
        parentTranslationPath={parentTranslationPath}
        isLoading={isLoading}
        onPageIndexChanged={(pageIndex) =>
          setFilter({ ...filter, pageIndex: pageIndex })
        }
        isWithFilter
        filterData={filterData(t, reload).map((item) => ({
          ...item,
          key: item.input,
          filterType: item.filterType ?? TableFilterTypesEnum.textInput.key,
          searchableKey: item.searchableKey ?? item.input,
        }))}
        onPageSizeChanged={(pageSize) => setFilter({ ...filter, pageSize })}
        onFilterValuesChanged={onFilterValuesChanged}
        FilterDisabledButton
        itemsPerPage={filter.pageSize}
        activePage={filter.pageIndex}
        // defaultActions={[
        //   { enum: TableActions.delete.key },
        //   { enum: TableActions.edit.key },
        // ]}
        defaultActions={getTableActionsWithPermissions()}
        setSortBy={(data) =>
          setFilter((preFilter) => ({ ...preFilter, ...data }))
        }
        actionsOptions={{
          onActionClicked: (actionEnum, item) => {
            setActiveItem(item);
            if (actionEnum === TableActions.edit.key) {
              setManagementDialogOpen(true);
            } else if (actionEnum === TableActions.delete.key) {
              setDeleteOfferDialogOpen(true);
            }
          },
        }}
      />
      {managementDialogOpen && (
        <OfferManagementDialog
          open
          onClose={() => {
            setManagementDialogOpen(false);
            setActiveItem(null);
          }}
          parentTranslationPath={parentTranslationPath}
          reload={reload}
          activeItem={activeItem}
        />
      )}
      {deleteOfferDialogOpen && (
        <DeleteOfferDialog
          activeItem={activeItem}
          open
          onClose={() => {
            setDeleteOfferDialogOpen(false);
            setActiveItem(null);
          }}
          reload={reload}
        />
      )}
    </div>
  );
};
export default Offers;
